import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const useStyles = makeStyles((theme) => ({
  dialog: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
  },
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default
  },
  header: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  contentContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2)
    }
  },
  contentInner: {
    maxWidth: 800,
    margin: '0 auto'
  },
  stepContent: {},
  footer: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      padding: theme.spacing(2)
    }
  },
  footerActions: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      '& > button': {
        flex: 1
      }
    }
  },
  field: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.6
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1)
  },
  progressText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  completedIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(0.5)
  },
  photoUploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(4)
  },
  avatar: {
    width: 200,
    height: 200,
    fontSize: '4rem',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    border: `4px solid ${theme.palette.divider}`
  },
  uploadButton: {
    minWidth: 200
  },
  hiddenInput: {
    display: 'none'
  }
}));

const ProfileOnboardingStepper = ({
  onSave,
  initialData,
  loading,
  photoUrl,
  onPhotoUpload,
  userName
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    headline: '',
    currentPosition: '',
    education: '',
    city: '',
    postalCode: '',
    location: '',
    contactEmail: '',
    linkedinUrl: '',
    about: ''
  });
  const [uploadedPhoto, setUploadedPhoto] = useState(null);

  const steps = [
    { label: 'Photo', field: 'photo', optional: false },
    { label: 'Headline', field: 'headline', optional: false },
    { label: 'Current Position', field: 'currentPosition', optional: true },
    { label: 'Education', field: 'education', optional: true },
    { label: 'City', field: 'city', optional: true },
    { label: 'Postal Code', field: 'postalCode', optional: true },
    { label: 'Location', field: 'location', optional: true },
    { label: 'Contact Email', field: 'contactEmail', optional: true },
    { label: 'LinkedIn URL', field: 'linkedinUrl', optional: true },
    { label: 'About', field: 'about', optional: true }
  ];

  // Only populate form data once on mount, not when initialData changes
  // This prevents form reset when parent component refetches profile data
  useEffect(() => {
    if (initialData) {
      setFormData({
        headline: initialData.headline || '',
        currentPosition: initialData.currentPosition || '',
        education: initialData.education || '',
        city: initialData.city || '',
        postalCode: initialData.postalCode || '',
        location: initialData.location || '',
        contactEmail: initialData.contactEmail || '',
        linkedinUrl: initialData.linkedinUrl || '',
        about: initialData.about || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleChange = (field) => (event) => {
    const newValue = event.target.value;
    const config = getFieldConfig(field);

    // Apply maxLength validation if specified
    if (config.maxLength && newValue.length > config.maxLength) {
      return; // Don't update if exceeds max length
    }

    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleNext = () => {
    // Just move to next step without saving
    if (activeStep < steps.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      // On last step, finish instead
      handleFinish();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSkip = () => {
    if (steps[activeStep].optional) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleFinish = () => {
    // Save all data only when user clicks Finish on the last step
    onSave(formData, true); // true = close dialog after save
  };

  const handleKeyPress = (event) => {
    // For multiline fields, only trigger on Ctrl+Enter or Cmd+Enter
    const currentStep = steps[activeStep];
    const field = currentStep.field;
    const config = getFieldConfig(field);

    if (event.key === 'Enter') {
      if (config.multiline) {
        // For multiline, only submit with Ctrl+Enter or Cmd+Enter
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          if (isStepValid()) {
            handleNext();
          }
        }
      } else {
        // For single-line inputs, submit on Enter
        event.preventDefault();
        if (isStepValid()) {
          handleNext();
        }
      }
    }
  };

  const getFieldConfig = (field) => {
    switch (field) {
      case 'photo':
        return {
          label: 'Profile Photo *',
          placeholder: '',
          helperText:
            'Upload a professional photo to make a great first impression',
          multiline: false,
          rows: 1,
          title: "Let's start with your profile photo",
          description:
            'A professional photo helps employers connect with you and makes your profile stand out.'
        };
      case 'headline':
        return {
          label: 'Professional Headline *',
          placeholder: 'e.g., Senior Software Engineer | Full-Stack Developer',
          helperText:
            'A catchy headline that summarizes your professional identity',
          multiline: false,
          rows: 1,
          title: "What's your professional headline?",
          description:
            'Create a compelling headline that showcases your expertise and role.'
        };
      case 'currentPosition':
        return {
          label: 'Current or Most Recent Position',
          placeholder: 'e.g., Senior Developer at Tech Company',
          helperText: '',
          multiline: false,
          rows: 1,
          title: 'What is your current or most recent position?',
          description:
            'Help employers understand your latest role and experience level.'
        };
      case 'education':
        return {
          label: 'Education',
          placeholder: 'e.g., BS Computer Science, MIT',
          helperText: '',
          multiline: false,
          rows: 1,
          title: "What's your educational background?",
          description: 'Share your highest degree or most relevant education.'
        };
      case 'city':
        return {
          label: 'City',
          placeholder: 'e.g., San Francisco',
          helperText: '',
          multiline: false,
          rows: 1,
          title: 'Which city are you based in?',
          description: 'Let employers know your current city location.'
        };
      case 'postalCode':
        return {
          label: 'Postal Code',
          placeholder: 'e.g., 94102',
          helperText: '',
          multiline: false,
          rows: 1,
          title: "What's your postal code?",
          description:
            'Provide your postal code for more specific location matching.'
        };
      case 'location':
        return {
          label: 'Country/State',
          placeholder: 'e.g., CA, USA',
          helperText: '',
          multiline: false,
          rows: 1,
          title: 'Where are you located?',
          description:
            'Specify your country or state for broader location visibility.'
        };
      case 'contactEmail':
        return {
          label: 'Contact Email',
          placeholder: 'your.email@example.com',
          helperText: '',
          multiline: false,
          rows: 1,
          type: 'email',
          title: "What's your contact email?",
          description: 'Provide an email where employers can reach you.'
        };
      case 'linkedinUrl':
        return {
          label: 'LinkedIn Profile URL',
          placeholder: 'https://linkedin.com/in/yourprofile',
          helperText: 'Your professional LinkedIn profile',
          multiline: false,
          rows: 1,
          title: 'Do you have a LinkedIn profile?',
          description:
            'Share your LinkedIn profile to showcase your professional network.'
        };
      case 'about':
        return {
          label: 'About',
          placeholder:
            'Tell us about yourself, your experience, and what makes you unique...',
          helperText: `${
            formData.about?.length || 0
          }/${(2600).toLocaleString()} characters. Press Ctrl+Enter (or Cmd+Enter) to finish.`,
          multiline: true,
          rows: 8,
          title: 'Tell us about yourself',
          description:
            "Share your professional background, skills, interests, and what you're looking for in your next role.",
          maxLength: 2600
        };
      default:
        return {
          label: field,
          placeholder: '',
          helperText: '',
          multiline: false,
          rows: 1,
          title: 'Enter your information',
          description: ''
        };
    }
  };

  const isStepValid = () => {
    const currentStep = steps[activeStep];
    // Check if step is required
    if (!currentStep.optional) {
      // Photo step validation
      if (currentStep.field === 'photo') {
        return !!(uploadedPhoto || photoUrl);
      }
      // Other required fields
      const fieldValue = formData[currentStep.field];
      return fieldValue && fieldValue.trim().length > 0;
    }
    // All optional steps are valid (can be empty)
    return true;
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedPhoto(file);
      // Call parent's onPhotoUpload handler
      if (onPhotoUpload) {
        onPhotoUpload(file);
      }
    }
  };

  const getUserInitials = (name) => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getStepContent = (step) => {
    const currentStep = steps[step];
    const field = currentStep.field;
    const config = getFieldConfig(field);

    // Special handling for photo step
    if (field === 'photo') {
      const displayPhotoUrl = uploadedPhoto
        ? URL.createObjectURL(uploadedPhoto)
        : photoUrl;
      return (
        <Box className={classes.stepContent}>
          <Typography variant="h4" className={classes.sectionTitle}>
            {config.title}
          </Typography>
          {config.description && (
            <Typography variant="body1" className={classes.sectionDescription}>
              {config.description}
            </Typography>
          )}
          <Box className={classes.photoUploadContainer}>
            <Avatar src={displayPhotoUrl} className={classes.avatar}>
              {!displayPhotoUrl && getUserInitials(userName)}
            </Avatar>
            <input
              accept="image/*"
              className={classes.hiddenInput}
              id="photo-upload-input"
              type="file"
              onChange={handlePhotoChange}
            />
            <label htmlFor="photo-upload-input">
              <Button
                variant="contained"
                color="primary"
                component="span"
                startIcon={<CloudUploadIcon />}
                className={classes.uploadButton}
                size="large">
                {uploadedPhoto || photoUrl ? 'Change Photo' : 'Upload Photo'}
              </Button>
            </label>
            <Typography variant="caption" color="textSecondary">
              {config.helperText}
            </Typography>
          </Box>
        </Box>
      );
    }

    // Regular text field for all other steps
    return (
      <Box className={classes.stepContent}>
        <Typography variant="h4" className={classes.sectionTitle}>
          {config.title}
        </Typography>
        {config.description && (
          <Typography variant="body1" className={classes.sectionDescription}>
            {config.description}
          </Typography>
        )}
        <TextField
          fullWidth
          label={config.label}
          placeholder={config.placeholder}
          variant="outlined"
          value={formData[field] || ''}
          onChange={handleChange(field)}
          onKeyPress={handleKeyPress}
          helperText={config.helperText}
          multiline={config.multiline}
          minRows={config.multiline ? config.rows : undefined}
          error={
            config.maxLength &&
            (formData[field]?.length || 0) >= config.maxLength
          }
          type={config.type || 'text'}
          autoFocus
          className={classes.field}
        />
      </Box>
    );
  };

  const progressPercentage = ((activeStep + 1) / steps.length) * 100;

  return (
    <Box>
      <Box className={classes.container}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Complete Your Profile
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box
          style={{
            padding: '16px 24px',
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            style={{ height: 8, borderRadius: 4 }}
          />
          <Typography className={classes.progressText} style={{ marginTop: 8 }}>
            {Math.round(progressPercentage)}% complete
          </Typography>
        </Box>

        {/* Content */}
        <Box className={classes.contentContainer}>
          <Box className={classes.contentInner}>
            {getStepContent(activeStep)}
          </Box>
        </Box>

        {/* Footer */}
        <Box className={classes.footer}>
          <Box className={classes.footerActions}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}>
              Back
            </Button>
            {steps[activeStep].optional && activeStep < steps.length - 1 && (
              <Button onClick={handleSkip} color="default">
                Skip
              </Button>
            )}
          </Box>
          <Box className={classes.footerActions}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleFinish}
                disabled={loading}
                startIcon={<CheckCircleIcon />}
                size="large">
                {loading ? 'Saving...' : 'Finish & Save'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!isStepValid()}
                endIcon={<ArrowForwardIcon />}
                size="large">
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ProfileOnboardingStepper.propTypes = {
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
  photoUrl: PropTypes.string,
  onPhotoUpload: PropTypes.func,
  userName: PropTypes.string
};

ProfileOnboardingStepper.defaultProps = {
  initialData: {},
  loading: false,
  photoUrl: null,
  onPhotoUpload: null,
  userName: 'User'
};

export default ProfileOnboardingStepper;
